import { range } from "./lerp";

type FrequancyName = 'bass' | 'lowMid' | 'mid' | 'highMid' | 'treble';
type OctaveBands = {
    lo: number,
    ctr: number,
    hi: number,
}

export default class FFT {
    audioContext: AudioContext;
    analyser: AnalyserNode;
    input: AnalyserNode;
    fftMeter: GainNode;

    freqDomain: Uint8Array | Float32Array;
    timeDomain: Uint8Array | Float32Array;

    bass: Array<number>;
    lowMid: Array<number>;
    mid: Array<number>;
    highMid: Array<number>;
    treble: Array<number>;

    framesSinceLastPeak: number;
    cutoff: number;
    energy: number;
    penergy: number;
    currentValue: number;
    peakIsDetected: boolean;
    beatCount: number;
    beatFade: number;
    
    constructor(smoothing: number = 0.8, bins: number = 1024, audioContext: AudioContext) {
        this.audioContext = audioContext;
        this.input = this.analyser = this.audioContext.createAnalyser();
        this.fftMeter = this.audioContext.createGain();
        this.smooth(smoothing);
        this.bins = bins; 

        this.fftMeter.connect(this.analyser);
        this.freqDomain = new Uint8Array(this.analyser.frequencyBinCount);
        this.timeDomain = new Uint8Array(this.analyser.frequencyBinCount); 

        this.bass = [20, 140];
        this.lowMid = [140, 400];
        this.mid = [400, 2600];
        this.highMid = [2600, 5200];
        this.treble = [5200, 14000]; 

        this.framesSinceLastPeak = 0;
        this.cutoff = 0; 
        this.energy = 0;
        this.penergy = 0; 
        this.currentValue = 0;
        this.peakIsDetected = false;
        this.beatCount = 0;
        this.beatFade = 0;
    }

    get bins() {
        return this.analyser.fftSize / 2;
    }

    set bins(b: number) {
        this.analyser.fftSize = b * 2;
    }

    get smoothing() {
        return this.analyser.smoothingTimeConstant;
    }

    set smoothing(s: number) {
        this.analyser.smoothingTimeConstant = s;
    }

    get volume() {
        return this.getEnergy(20, 14000) / 255;
    }

    smooth(s: number) {
        if (typeof s !== 'undefined') {
            this.smoothing = s;
        }
        return this.smoothing;
    }

    setInput(source: MediaStreamAudioSourceNode | undefined) {
        if (!source) {
            this.fftMeter.connect(this.analyser);
        } else {
            source.connect(this.analyser);
            this.fftMeter.disconnect();
        }
    }

    freqToFloat() {
        if (this.freqDomain instanceof Float32Array === false) {
            this.freqDomain = new Float32Array(this.analyser.frequencyBinCount);
        }
    }

    freqToInt() {
        if (this.freqDomain instanceof Uint8Array === false) {
            this.freqDomain = new Uint8Array(this.analyser.frequencyBinCount);
        }
    }

    timeToFloat() {
        if (this.timeDomain instanceof Float32Array === false) {
            this.timeDomain = new Float32Array(this.analyser.frequencyBinCount);
        }
    }

    timeToInt() {
        if (this.timeDomain instanceof Uint8Array === false) {
            this.timeDomain = new Uint8Array(this.analyser.frequencyBinCount);
        }
    }

    waveform() {
        let bins, mode;
        const normalArray = new Array();
        for (let i = 0; i < arguments.length; i++) {
            if (typeof arguments[i] === 'number') {
                bins = arguments[i];
                this.analyser.fftSize = bins * 2;
            }
            if (typeof arguments[i] === 'string') {
                mode = arguments[i];
            }
        } 
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (mode && !isSafari) {
            this.timeToFloat();
            this.analyser.getFloatTimeDomainData(this.timeDomain as Float32Array);
            return this.timeDomain;
        } else {
            this.timeToInt();
            this.analyser.getByteTimeDomainData(this.timeDomain as Uint8Array);
            for (let j = 0; j < this.timeDomain.length; j++) {
                const scaled = range(0, 255, -1, 1, this.timeDomain[j]);
                normalArray.push(scaled);
            }
            return normalArray;
        }
    }

    analyze() {
        let mode;
        for (let i = 0; i < arguments.length; i++) {
            if (typeof arguments[i] === 'number') {
                this.bins = arguments[i];
                this.analyser.fftSize = this.bins * 2;
            }
            if (typeof arguments[i] === 'string') {
                mode = arguments[i];
            }
        }
        if (mode && mode.toLowerCase() === 'db') {
            this.freqToFloat();
            this.analyser.getFloatFrequencyData(this.freqDomain as Float32Array);
            return this.freqDomain;
        } else {
            this.freqToInt();
            this.analyser.getByteFrequencyData(this.freqDomain as Uint8Array);
            const normalArray = Array.from(this.freqDomain);
            return normalArray;
        }
    }

    getEnergy(frequency1: FrequancyName | number, frequency2: FrequancyName | number) {
        const nyquist = this.audioContext.sampleRate / 2;
        if (frequency1 === 'bass') {
            frequency1 = this.bass[0];
            frequency2 = this.bass[1];
        } else if (frequency1 === 'lowMid') {
            frequency1 = this.lowMid[0];
            frequency2 = this.lowMid[1];
        } else if (frequency1 === 'mid') {
            frequency1 = this.mid[0];
            frequency2 = this.mid[1];
        } else if (frequency1 === 'highMid') {
            frequency1 = this.highMid[0];
            frequency2 = this.highMid[1];
        } else if (frequency1 === 'treble') {
            frequency1 = this.treble[0];
            frequency2 = this.treble[1];
        }
        if (typeof frequency1 !== 'number' || typeof frequency2 !== 'number') {
            throw 'invalid input for getEnergy()';
        } else if (!frequency2) {
            const index = Math.round(frequency1 / nyquist * this.freqDomain.length);
            return this.freqDomain[index];
        } else if (frequency1 && frequency2) {
            if (frequency1 > frequency2) {
                const swap = frequency2;
                frequency2 = frequency1;
                frequency1 = swap;
            }
            const lowIndex = Math.round(frequency1 / nyquist * this.freqDomain.length);
            const highIndex = Math.round(frequency2 / nyquist * this.freqDomain.length);
            let total = 0;
            let numFrequencies = 0; 
            for (let i = lowIndex; i <= highIndex; i++) {
                total += this.freqDomain[i];
                numFrequencies += 1;
            } 
            const toReturn = total / numFrequencies;
            return toReturn;
        } else {
            throw 'invalid input for getEnergy()';
        }
    } 

    getFreq(freq1: FrequancyName | number, freq2: FrequancyName | number) {
        console.log('getFreq() is deprecated. Please use getEnergy() instead.');
        const x = this.getEnergy(freq1, freq2);
        return x;
    }

    getCentroid() {
        const nyquist = this.audioContext.sampleRate / 2;
        let cumulative_sum = 0;
        let centroid_normalization = 0;
        for (let i = 0; i < this.freqDomain.length; i++) {
            cumulative_sum += i * this.freqDomain[i];
            centroid_normalization += this.freqDomain[i];
        }
        let mean_freq_index = 0;
        if (centroid_normalization !== 0) {
            mean_freq_index = cumulative_sum / centroid_normalization;
        }
        const spec_centroid_freq = mean_freq_index * (nyquist / this.freqDomain.length);
        return spec_centroid_freq;
    }

    dispose(): void {
        if (this.analyser) {
            this.analyser.disconnect();
        }
        if (this.audioContext) {
            this.audioContext.close();
        }
    }

    linAverages(N = 16) {
        const spectrum = this.freqDomain;
        const spectrumLength = spectrum.length;
        const spectrumStep = Math.floor(spectrumLength / N);
        const linearAverages = new Array(N); 
        let groupIndex = 0;
        for (let specIndex = 0; specIndex < spectrumLength; specIndex++) {
            linearAverages[groupIndex] = linearAverages[groupIndex] !== undefined ? (linearAverages[groupIndex] + spectrum[specIndex]) / 2 : spectrum[specIndex]; 
            if (specIndex % spectrumStep === spectrumStep - 1) {
                groupIndex++;
            }
        }
        return linearAverages;
    }

    getOctaveBands(N = 3, fCtr0 = 15.625) {
        const octaveBands: Array<OctaveBands> = [];
        let lastFrequencyBand = {
            lo: fCtr0 / Math.pow(2, 1 / (2 * N)),
            ctr: fCtr0,
            hi: fCtr0 * Math.pow(2, 1 / (2 * N))
        };
        octaveBands.push(lastFrequencyBand);
        const nyquist = this.audioContext.sampleRate / 2;
        while (lastFrequencyBand.hi < nyquist) {
            const newFrequencyBand: OctaveBands = {
                lo: lastFrequencyBand.hi,
                ctr: lastFrequencyBand.ctr * Math.pow(2, 1 / N),
                hi: lastFrequencyBand.ctr * Math.pow(2, 1 / N) * Math.pow(2, 1 / (2 * N)),
            };
            octaveBands.push(newFrequencyBand);
            lastFrequencyBand = newFrequencyBand;
        }
        return octaveBands;
    }

    logAverages(octaveBands: Array<OctaveBands>) {
        var nyquist = this.audioContext.sampleRate / 2;
        var spectrum = this.freqDomain;
        var spectrumLength = spectrum.length;
        var logAverages = new Array(octaveBands.length); 
        var octaveIndex = 0;
        for (var specIndex = 0; specIndex < spectrumLength; specIndex++) {
            var specIndexFrequency = Math.round(specIndex * nyquist / this.freqDomain.length); 
            if (specIndexFrequency > octaveBands[octaveIndex].hi) {
                octaveIndex++;
            }
            logAverages[octaveIndex] = logAverages[octaveIndex] !== undefined ? (logAverages[octaveIndex] + spectrum[specIndex]) / 2 : spectrum[specIndex];
        }
        return logAverages;
    }

    detectBeat(freq1: number | FrequancyName = 20, freq2: number | FrequancyName = 400, threshold = 0.035, framesPerPeak = 20) {
        const decayRate = 0.95;
        const cutoffMult = 1.5;
        const nrg = this.energy = this.getEnergy(freq1, freq2) / 255;
        //console.log(this.beatFade)
        if (nrg > this.cutoff && nrg > threshold && nrg - this.penergy > 0) {
            this.beatFade = 1;
            this.beatCount += 1;
            //console.log('beat detected')
            this.peakIsDetected = true; 
            this.cutoff = nrg * cutoffMult;
            this.framesSinceLastPeak = 0;
        } else {
            this.beatFade -= 1/framesPerPeak;
            this.beatFade = this.beatFade < 0 ? 0 : this.beatFade
            this.peakIsDetected = false;
            if (this.framesSinceLastPeak <= framesPerPeak) {
                this.framesSinceLastPeak++;
            } else {
                this.cutoff *= decayRate;
                this.cutoff = Math.max(this.cutoff, threshold);
            }
        }
        this.currentValue = nrg;
        this.penergy = nrg;
    }
}